import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Alert, Badge, Box, Button, Icon, Popover, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { TPEReactTable, TPETableFooterRow } from 'src/components/shared/TPEReactTable';
import { renderExpressionParts } from 'src/components/shared/FormulaExpressionFunctionComponents';
import ErrorUtils from 'src/utils/ErrorUtils';
import CONSTANTS from 'src/utils/constants';
import {
    CommonBalancePoolColumn,
    CommonBalancePoolColumnDataType,
} from 'src/models/multidimensional-allocation/CommonBalancePool';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import { effectiveDefinitionColumns, isPeriodEditable, periodDefinitionState, PERIOD_DEFINITION_UNAVAILABLE } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderState';
import { CURRENT_NUMBERS_LABEL, isRunSelection, runLabel, toExecutionIdParam } from 'src/components/shared/execution-selector/executionSelection';
import { toExpressionParts } from './cbpFormula';
import { withNames } from './cbpMessages';
import 'src/assets/styles/react-table.scss';

// A CBP can reach ~10k rows (LLD §7.5); the materialized results page in a bounded chunk at a time,
// with a "Load more" driven by the response's nextToken.
const ROWS_PAGE_SIZE = 50;

const RUN_DEFINITION_UNAVAILABLE = 'The definition this run computed with is unavailable.';

/** UI label for a CBP grain, mirroring AddCbpInputModal (COST_CENTER -> "Cost Center"). */
const grainUiLabel = (grain?: string): string =>
    (grain
        ? (CONSTANTS.COA_SEGMENT_MAPPING as Record<string, { UI: string }>)[grain]?.UI ?? grain
        : 'Grain');

/** "Cost Center" -> "cost centers" for the count caption + totals label. */
const grainPluralLower = (grain?: string): string => {
    const label = grainUiLabel(grain).toLowerCase();
    return label.endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;
};

/** Storage is always numeric; PERCENTAGE renders ×100 + "%". A missing/NaN value renders as an en dash. */
const formatCbpValue = (value: number | undefined, dataType?: CommonBalancePoolColumnDataType): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '–';
    if (dataType === 'PERCENTAGE') {
        return `${(value * 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

/**
 * The source sub-label under a column header (per the LLD's sourced/derived lineage): a CALC column is
 * "Calculated"; an INPUT column shows its source object ("CDT | <table>" / "MDA | <sheet>"). Derived
 * from the column's own kind/sourceType/sourceRef — nothing is hard-coded.
 */
const columnSourceLabel = (col: CommonBalancePoolColumn): string => {
    if (col.kind === 'CALC') return 'Calculated';
    if (col.sourceType === 'SEGMENT_CDT') {
        const src = col.sourceRef?.cdtTableName ?? col.sourceRef?.cdtTableId;
        return src ? `CDT | ${src}` : 'CDT';
    }
    if (col.sourceType === 'MDA_OUTPUT') {
        const src = col.sourceRef?.mdaSheetName ?? col.sourceRef?.mdaSheetId;
        return src ? `MDA | ${src}` : 'MDA';
    }
    return '';
};

/** A two-line column header: the display name over a secondary source label (+ optional info popover). */
const columnHeader = (title: string, subLabel: string, info?: React.ReactNode) => (
    <div className="cell-text-non-sortable">
        <div>{title}</div>
        <Box fontSize="body-s" color="text-body-secondary">
            {subLabel}
            {info && (
                <Popover dismissButton={false} position="top" size="medium" triggerType="custom" content={info}>
                    {' '}
                    <Icon name="status-info" size="small" variant="link" />
                </Popover>
            )}
        </Box>
    </div>
);

// A grid row: the grain value, its name, plus its derived (CALC) column values.
type CbpGridRow = Record<string, any> & { grainValue: string; grainDescription?: string };

/**
 * Step 3 — Preview. Shows the pool's **materialized results for the period selected in the
 * top status bar**: one row per grain value, a column per derived (CALC) column (formatted by its data
 * type). Data comes from a single paginated read — GetCommonBalancePoolRows(poolId, period) — re-fetched
 * from the first page whenever the period changes, so selecting a past (closed) period shows that period's
 * immutable results. Download exports the period's rows to a spreadsheet via a presigned URL
 * (DownloadCommonBalancePool). Each row's grain description is the name stored on the row when the period was
 * materialised. The pinned Total row shows the period's column totals as the publishing run stamped them.
 * With a MEC or dry run picked in the header's Run selector, the grid and Download show that run's own copy.
 */
export default function CbpResultsSection() {
    const { cbpBuilderState, cbpBuilderDispatch, services, poolId } = useContext(CommonBalancePoolBuilderContext);
    // The Step-3 preview slice lives in the builder reducer (per the package convention), not as
    // component-local state; this section reads it from context and dispatches the RESULTS_* actions.
    const {
        pool,
        period,
        resultsRows: rows,
        resultsNextToken: nextToken,
        resultsTotalRowCount: totalRowCount,
        resultsTotals: totals,
        resultsLoading,
        resultsError: error,
        resultsRefreshToken,
        resultsDownloading: downloading,
        resultsDownloadError: downloadError,
        executionSelection: selection,
        resultsRunDefinition: runDefinition,
    } = cbpBuilderState;
    const periodValue = period?.value;
    const periodLabel = period?.label ?? periodValue;
    const runSelected = isRunSelection(selection);
    const executionId = toExecutionIdParam(selection);
    // The period's run list is still loading: hold the rows read until the default selection is known.
    const selectionPending = !!periodValue && !selection;
    const loading = resultsLoading || selectionPending;

    // Only the most recent request's result is applied — guards a fast period switch / overlapping loads.
    // A mutable ref (transient async orchestration), not reducer state.
    const reqIdRef = useRef(0);

    // Per-period definition columns for the grid HEADERS + formula labels: the working definition for the
    // open (editable) period, else the version that produced the selected period (resultsVersionedColumns,
    // resolved by the effect below and held in the reducer per the package convention). Shared with Steps 1-2
    // via effectiveDefinitionColumns so all three steps show the same period-appropriate definition. The
    // resolve effect does not run on paging (its deps are the period, not the page token).
    // A run's rows render under the definition it computed with; Steps 1-2 stay on the period's definition.
    const effectiveColumns: CommonBalancePoolColumn[] = useMemo(
        () => (runSelected ? runDefinition?.columns ?? [] : effectiveDefinitionColumns(cbpBuilderState)),
        [cbpBuilderState.pool, cbpBuilderState.resultsVersionedColumns, cbpBuilderState.period, cbpBuilderState.openPeriod, runSelected, runDefinition],
    );

    // Product decision (2026-09): the preview shows only DERIVED (CALC) columns — input columns are NOT
    // shown — alongside the grain value. A materialized ROW# still carries a value for every column, so we
    // simply render the CALC subset. Headers are the SELECTED PERIOD's definition (effectiveColumns), so a
    // closed period shows the formulas that produced it, not today's draft.
    const columns: CommonBalancePoolColumn[] = useMemo(
        () => effectiveColumns
            .filter((col) => col.kind === 'CALC')
            .sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0)),
        [effectiveColumns],
    );

    const periodEditable = isPeriodEditable(cbpBuilderState);
    const defState = periodDefinitionState(cbpBuilderState);
    // Which row lane to read: a read-only PAST period reads the published LIVE lane (version 0) so its
    // materialized rows show even while the pool has a draft — otherwise the default (working) read would hit
    // the draft lane, which holds only the open period's preview and is empty for a past period. The open
    // period keeps the working default (its draft preview when a draft exists, else live).
    // A run pins its own definition, so it is read without a lane (the server refuses both).
    const rowsVersion = runSelected || periodEditable ? undefined : 0;

    // The single per-period read for Step 3 (no separate "light preview"): one paginated
    // GetCommonBalancePoolRows keyed on the selected period. `append` adds the next page; otherwise it
    // replaces (first page). Runs inside try/catch so a synchronous failure becomes the error state.
    const fetchRows = useCallback(async (token: string | undefined, append: boolean) => {
        if (!periodValue) return;
        const reqId = reqIdRef.current + 1;
        reqIdRef.current = reqId;
        cbpBuilderDispatch(ACTIONS.RESULTS_LOADING.withPayload(undefined));
        try {
            const resp = await services.commonBalancePoolService.getCommonBalancePoolRows(poolId, periodValue, ROWS_PAGE_SIZE, token, rowsVersion, executionId);
            if (reqId !== reqIdRef.current) return; // a newer request superseded this one
            if (executionId && !append) {
                // Fail loudly rather than show a run's rows under another version's headers.
                // 0 = the run used the live pointer (as the server's run Download resolves it); -1 (draft) is never a run's.
                const version = resp.definitionVersion;
                const columns = version != null && version >= 0
                    ? (await services.commonBalancePoolService.getCommonBalancePool(poolId, version))?.columns
                    : undefined;
                if (reqId !== reqIdRef.current) return;
                if (version == null || !columns?.length) throw new Error(RUN_DEFINITION_UNAVAILABLE);
                cbpBuilderDispatch(ACTIONS.RESULTS_RUN_DEFINITION.withPayload({ version, columns }));
            }
            cbpBuilderDispatch(ACTIONS.RESULTS_LOADED.withPayload({
                rows: resp.rows ?? [],
                nextToken: resp.nextToken,
                totalRowCount: resp.totalRowCount,
                totals: resp.totals,
                append,
            }));
        } catch (ex) {
            if (reqId === reqIdRef.current) cbpBuilderDispatch(ACTIONS.RESULTS_ERROR.withPayload(ErrorUtils.getMessage(ex)));
        }
    }, [periodValue, poolId, services, cbpBuilderDispatch, rowsVersion, executionId]);

    // (Re)load the first page whenever the selected period (or pool) changes, or when a refresh is forced
    // (resultsRefreshToken — e.g. after an Execute); clear stale rows first so a period switch never shows
    // the previous period's data.
    useEffect(() => {
        // Supersede any read still in flight before the pending check: while a new period's run list loads no
        // read starts, so without this an older period's read could land and fill the grid.
        reqIdRef.current += 1;
        cbpBuilderDispatch(ACTIONS.RESULTS_RESET.withPayload(undefined));
        if (periodValue && !selectionPending) fetchRows(undefined, false);
        // rowsVersion is a dep so a lane flip that is NOT accompanied by a period change (e.g. openPeriod or
        // the resolved version arriving late) refetches from the correct lane rather than leaving stale rows.
        // A selection change restarts the cursor: a nextToken belongs to one copy.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodValue, poolId, resultsRefreshToken, rowsVersion, executionId, selectionPending]);

    // While a run is shown on the editable period, probe the shared rows so Submit for review still reflects
    // the user's own Execute rather than the run copy on screen.
    useEffect(() => {
        if (!periodValue || !runSelected || !periodEditable) return undefined;
        let active = true;
        services.commonBalancePoolService.getCommonBalancePoolRows(poolId, periodValue, 1, undefined, undefined)
            .then((resp) => {
                if (active) cbpBuilderDispatch(ACTIONS.RESULTS_SHARED_HAS_ROWS.withPayload((resp.rows ?? []).length > 0 || (resp.totalRowCount ?? 0) > 0));
            })
            .catch(() => {
                if (active) cbpBuilderDispatch(ACTIONS.RESULTS_SHARED_HAS_ROWS.withPayload(false));
            });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodValue, poolId, runSelected, periodEditable, resultsRefreshToken]);

    // Resolve the definition version that PRODUCED the selected period, and render its columns as the grid's
    // headers/formulas. The current_version provenance shipped in ICTransferPricingScheduledJobs (ffc5200), so
    // a published period reports its own version (>= 1) and GetCommonBalancePool?version=N returns it. Two
    // cases still yield no version: a period published BEFORE that provenance (lane `0`, no backfill), or an
    // unmaterialised period (GetCommonBalancePoolPeriod rejects). Both leave a PAST period showing its
    // "definition unavailable" state (never the working definition). GetCommonBalancePoolPeriod answering at
    // all (vs throwing) is the authoritative "is this period published" signal → resultsPeriodMaterialised.
    useEffect(() => {
        cbpBuilderDispatch(ACTIONS.RESULTS_VERSIONED_COLUMNS.withPayload(null));
        cbpBuilderDispatch(ACTIONS.RESULTS_PERIOD_VERSION.withPayload(undefined));
        cbpBuilderDispatch(ACTIONS.RESULTS_PERIOD_MATERIALISED.withPayload(undefined));
        if (!periodValue) {
            cbpBuilderDispatch(ACTIONS.RESULTS_VERSION_RESOLVING.withPayload(false));
            return undefined;
        }
        cbpBuilderDispatch(ACTIONS.RESULTS_VERSION_RESOLVING.withPayload(true));
        // `active` (cleared by the cleanup on a period switch / unmount) drops a late resolve so a slower
        // earlier period cannot overwrite a newer one's columns.
        let active = true;
        let periodResolved = false;
        (async () => {
            try {
                const { definitionVersion } = await services.commonBalancePoolService.getCommonBalancePoolPeriod(poolId, periodValue);
                if (!active) return;
                periodResolved = true;
                cbpBuilderDispatch(ACTIONS.RESULTS_PERIOD_MATERIALISED.withPayload(true)); // answered => published
                if (definitionVersion == null || definitionVersion < 1) return; // lane 0 / pre-provenance: no version
                const versionPool = await services.commonBalancePoolService.getCommonBalancePool(poolId, definitionVersion);
                const cols = versionPool?.columns;
                if (active && cols?.length) {
                    cbpBuilderDispatch(ACTIONS.RESULTS_VERSIONED_COLUMNS.withPayload(cols));
                    cbpBuilderDispatch(ACTIONS.RESULTS_PERIOD_VERSION.withPayload(definitionVersion));
                }
            } catch (ex) {
                // getPeriod rejecting => unmaterialised period; a later version-fetch failure leaves the period
                // materialised. Warn either way so a silent fallback is visible (matches the reducer's default).
                if (active && !periodResolved) cbpBuilderDispatch(ACTIONS.RESULTS_PERIOD_MATERIALISED.withPayload(false));
                // eslint-disable-next-line no-console
                console.warn('CBP: could not resolve the selected period’s definition version', ex);
            } finally {
                if (active) cbpBuilderDispatch(ACTIONS.RESULTS_VERSION_RESOLVING.withPayload(false));
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodValue, poolId, resultsRefreshToken]);

    const reloadFirstPage = useCallback(() => fetchRows(undefined, false), [fetchRows]);
    const loadMore = useCallback(() => fetchRows(nextToken, true), [fetchRows, nextToken]);

    // Download: fetch a presigned URL for the period's materialised rows and open it. The URL is one-shot,
    // so we navigate to it rather than storing it; a failure surfaces as a dismissible error.
    const handleDownload = useCallback(async () => {
        if (!periodValue) return;
        cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_START.withPayload(undefined));
        try {
            const resp = await services.commonBalancePoolService.downloadCommonBalancePool(poolId, periodValue, rowsVersion, executionId);
            if (resp?.downloadUrl) window.open(resp.downloadUrl, '_blank', 'noopener,noreferrer');
            cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(undefined));
        } catch (ex) {
            cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(ErrorUtils.getMessage(ex)));
        }
    }, [periodValue, poolId, services, cbpBuilderDispatch, rowsVersion, executionId]);

    const hasRows = rows.length > 0;
    const grainPlural = grainPluralLower(pool?.grain);

    // Draft-vs-live lane indicator: while the pool is a DRAFT the previewed rows are a draft-lane preview
    // (not consumed by downstream allocations); once ACTIVE they are the published, live results. Derived
    // from pool.status alone (no model change) — the two states the lifecycle pins. Other statuses
    // (PENDING_APPROVAL / INACTIVE) show no badge rather than assert a lane.
    // A run copy belongs to no lane; the read-only banner describes it instead.
    const laneBadge = runSelected ? null : pool?.status === 'DRAFT_IN_PROGRESS'
        ? <Badge color="blue">Draft preview</Badge>
        : pool?.status === 'ACTIVE'
            ? <Badge color="green">Live</Badge>
            : null;

    // GetCommonBalancePoolRows deliberately omits totalRowCount (a page-size "total" would grow as the user
    // pages — "50 of 50", then "100 of 100"). So show the "of N" clause only when the backend actually
    // supplies a total; otherwise report just the loaded count.
    const countCaption = totalRowCount != null
        ? `Showing ${rows.length.toLocaleString('en-US')} of ${totalRowCount.toLocaleString('en-US')} ${grainPlural} for ${periodLabel}.`
        : `Showing ${rows.length.toLocaleString('en-US')} ${grainPlural} for ${periodLabel}.`;

    // One row per grain value with its stored name; its derived (CALC) column values are spread in.
    const gridData: CbpGridRow[] = useMemo(
        () => rows.map((r) => ({ grainValue: r.grainValue, grainDescription: r.grainDescription, ...r.values })),
        [rows],
    );

    // columnId -> display name over ALL columns (inputs + calc) of the SELECTED PERIOD's definition, so a
    // CALC column's info popover renders its formula with the referenced inputs' names (of that version),
    // not raw column ids.
    const nameById = useMemo(
        () => Object.fromEntries(effectiveColumns.map((c) => [c.columnId, c.displayName || c.columnId])),
        [effectiveColumns],
    );

    // Headers from the pool DEFINITION + the grain (nothing hard-coded): the grain value, its description,
    // then every derived (CALC) pool column in order, each with a source sub-label.
    const columnDefinitions = useMemo(() => {
        const grainLabel = grainUiLabel(pool?.grain);
        return [
            {
                accessor: 'grainValue',
                Header: () => columnHeader(grainLabel, 'FRAMES'),
                Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
            },
            {
                // Stored on the row at materialisation from the pool's segment data tables. A row materialised
                // before names were stored has none until its period is re-run, so it shows the missing-value dash.
                accessor: 'grainDescription',
                Header: () => columnHeader(`${grainLabel} Description`, 'Segment data table'),
                Cell: ({ value }: any) => <div className="cell-text">{value || '–'}</div>,
            },
            ...columns.map((col) => ({
                accessor: col.columnId,
                Header: () => columnHeader(
                    col.displayName || col.columnId,
                    columnSourceLabel(col),
                    col.kind === 'CALC' && col.formula ? renderExpressionParts(toExpressionParts(col.formula, nameById)) : undefined,
                ),
                Cell: ({ value }: any) => <div className="cell-text">{formatCbpValue(value, col.dataType)}</div>,
            })),
        ];
    }, [columns, pool?.grain, nameById]);

    // The pinned Total row. The figures are the period's server-computed totals (resultsTotals), never a sum of
    // the loaded pages — only the first 50 rows are here until "Load more". A column the run did not total (a
    // PERCENTAGE ratio) or a period stamped before totals existed renders the same dash as a missing cell.
    // Omitted entirely when there are no totals, so a draft preview shows no Total row rather than a row of dashes.
    // The label names how many rows the totals cover — the period's count, as the publishing run stamped it
    // (totalRowCount), never the number of rows loaded so far.
    const footerRows: TPETableFooterRow[] = useMemo(() => {
        if (!totals || Object.keys(totals).length === 0) return [];
        const totalLabel = totalRowCount != null
            ? `Total (${totalRowCount.toLocaleString('en-US')} ${totalRowCount === 1
                ? grainUiLabel(pool?.grain).toLowerCase()
                : grainPluralLower(pool?.grain)})`
            : 'Total';
        return [{
            className: 'cbpTotalRow',
            cells: [
                // Spans the grain value and its description, so each figure sits under its own column.
                { content: totalLabel, colSpan: 2, className: 'cbpFooterLabel' },
                ...columns.map((col) => ({
                    content: formatCbpValue(totals[col.columnId], col.dataType),
                    className: 'cbpFooterValue',
                })),
            ],
        }];
    }, [totals, totalRowCount, columns, pool?.grain]);

    const errorAlert = (
        <Alert type="error" dismissible header="Could not load results" onDismiss={() => cbpBuilderDispatch(ACTIONS.RESULTS_ERROR.withPayload(undefined))}>
            <SpaceBetween direction="vertical" size="xs">
                <Box>{withNames(error, effectiveColumns, cbpBuilderState.pool)}</Box>
                <Box><Button iconName="refresh" onClick={reloadFirstPage}>Retry</Button></Box>
            </SpaceBetween>
        </Alert>
    );

    return (
        <SpaceBetween direction="vertical" size="m">
            <Box color="text-body-secondary">
                Preview the materialized CBP for the selected period.
            </Box>

            {/* "Materialized preview" heading on the left, the Download action on the right. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <Box variant="h4" padding={{ top: 'n' }}>Materialized preview</Box>
                    {laneBadge}
                </SpaceBetween>
                <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={handleDownload} loading={downloading} disabled={downloading || !hasRows}>
                        Download
                    </Button>
                </SpaceBetween>
            </div>

            {/* Download failures surface here (the button is disabled until there are rows to export). */}
            {downloadError && (
                <Alert type="error" dismissible header="Could not download"
                    onDismiss={() => cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(undefined))}>
                    {withNames(downloadError, effectiveColumns, cbpBuilderState.pool)}
                </Alert>
            )}

            {runSelected && (
                <Alert type="info" header={`Showing ${runLabel(selection.run)} (read-only)`}>
                    {runDefinition && runDefinition.version >= 1 ? `Computed with version v${runDefinition.version}. ` : ''}
                    Choose {CURRENT_NUMBERS_LABEL} under Run to see the latest Execute results.
                </Alert>
            )}

            {/* A run's headers arrive with its first page; until then show progress, or the read error. */}
            {runSelected && columns.length === 0 && (
                periodValue && !loading && error
                    ? errorAlert
                    : <StatusIndicator type={loading ? 'loading' : 'info'}>
                        {loading ? 'Loading the selected run…' : RUN_DEFINITION_UNAVAILABLE}
                    </StatusIndicator>
            )}

            {/* No columns to show. Distinguish the three cases: still resolving; a read-only PAST period whose
                version could not be resolved (unavailable — not "configure inputs", which it can't do); or an
                editable pool genuinely without columns yet. */}
            {!runSelected && columns.length === 0 && (
                <StatusIndicator type={defState === 'resolving' ? 'loading' : 'info'}>
                    {defState === 'resolving'
                        ? 'Loading the selected period’s definition…'
                        : defState === 'unavailable'
                            ? PERIOD_DEFINITION_UNAVAILABLE
                            : 'Configure inputs (Step 1) and calculated columns (Step 2) to preview the table.'}
                </StatusIndicator>
            )}

            {/* The grid renders whenever the pool has columns: HEADERS come from the definition (visible
                immediately); the VALUES are the selected period's materialized rows, filled in by the
                paginated GetCommonBalancePoolRows read. A status line explains the state. */}
            {columns.length > 0 && (
                <>
                    {!periodValue && (
                        <StatusIndicator type="info">Select a period (top of the page) to load results.</StatusIndicator>
                    )}
                    {periodValue && loading && !hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">Loading results for {periodLabel}…</Box>
                    )}
                    {periodValue && !loading && error && errorAlert}
                    {periodValue && !error && hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">{countCaption}</Box>
                    )}
                    {periodValue && !loading && !error && !hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">
                            {runSelected
                                ? `This run kept no rows for ${periodLabel}.`
                                : `No materialized rows for ${periodLabel}. Execute this pool for the period to populate results.`}
                        </Box>
                    )}

                    {/* A pool can have many derived columns; keep a wide table scrolling WITHIN the step box
                        (overflow-x on this wrapper) rather than widening the step layout / the page. */}
                    <div className="cbpResultsGridScroll">
                        <TPEReactTable {...{
                            // Values only when the period returned rows; otherwise just the headers + empty message.
                            data: hasRows ? gridData : [],
                            columnDefinitions,
                            footerRows: hasRows ? footerRows : [],
                            // Real auto-layout table (see react-table.scss) so the variable column set lays out
                            // horizontally and the headers stay visible even with an empty body.
                            className: 'nonGridTable cbpResultsGrid',
                            sortable: false,
                            key: 'cbpResultsGrid',
                            emptyMessage: periodValue ? 'Results appear here once the period is executed.' : 'Select a period to load results.',
                        }} />
                    </div>

                    {nextToken && (
                        <Box textAlign="center">
                            <Button onClick={loadMore} loading={loading} disabled={loading}>Load more</Button>
                        </Box>
                    )}
                </>
            )}
        </SpaceBetween>
    );
}
